import React from 'react'
import { groupDataByMonth } from 'crm-utils/data-utils'
import { createDashboardFormats, useQueryActivity, getWidgetAnalyticContext } from '../../dashboards/tools'
import moment from 'moment'
import { DashboardResponse, GQLQueryRes, HeadCount } from 'crm-types'
import withLeadPopUpHoc from '../../components/_with-leads-popup-hoc'
import {
  convertMonthToDateRange,
  resolveDashboardWidgetFiltersFromContextFilters
} from 'crm-modules/dashboard/dashboard-utils'

export interface WidgetFollowUpMeetingStatusHocProps {
  avgFollowUpMeetingCurrentMonthPerPerson: number | null
  avgFollowUpMeetingPassedMonthData: number | any[any]
  avgFollowUpMeetingPersonPassedMonth: number | null
  avgFollowUpMeetingThreeMonth: number
  footerName: string | null
  footerNamePerPerson: string | null
  isLoading?: boolean
  refetchData?: () => any
  threeMonthAgoFollowUpMeeting: any[any]
  totalFollowUpMeeting: number
  onClickDetail: Function
  mainCaption?: string
}

export type UseGetFollowUpMeetingProspectDashboardByMonth = (
  numberOfMonth?: number,
  headcount?: HeadCount[]
) => DashboardResponse & GQLQueryRes

export const useGetFollowUpMeetingProspectDashboardByMonth: UseGetFollowUpMeetingProspectDashboardByMonth = (
  numberOfMonth,
  headcounts
) => {
  const analyticsContext = getWidgetAnalyticContext()
  const { DATE_FORMAT, endDate, userUid, organization_unit } = resolveDashboardWidgetFiltersFromContextFilters(
    analyticsContext
  )
  const startDate = moment(endDate, DATE_FORMAT)
    .subtract(numberOfMonth, 'month')
    .startOf('month')
    .format(DATE_FORMAT)

  const userID = !userUid ? undefined : { user_uid: { value: userUid } }
  const user = headcounts ? undefined : userID

  const filter = {
    organization_unit,
    user,
    role_code: 'performer',
    account_is_deleted: false,
    activity: {
      activity_type: {
        medium_code: 'meeting'
      },
      status: {
        is_success: true
      },
      is_customer: false,
      first_type: 'follow_up',
      start_date: {
        $gte: startDate,
        $lte: endDate
      }
    }
  }

  const { listActivity, refetch, loading } = useQueryActivity(
    filter,
    ['start_date_month'],
    [{ type: 'count', id: 'activity_uid' }]
  )

  const grouped = groupDataByMonth(listActivity, 'start_date_month', 'activity_uid')

  return {
    refetch,
    loading,
    ...createDashboardFormats(grouped, numberOfMonth, moment(endDate, 'YYYY-MM-DD').format('YYYY-MM'), headcounts)
  }
}

const widgetFollowUpMeetingStatusHoc = (Component: any) => {
  const Wrapper = props => {
    const { headcounts } = props
    const analyticsContext = getWidgetAnalyticContext()
    const {
      avgCurrentMonthPerPerson,
      avgPassedMonths,
      avgPerPersonPassedMonth,
      dataAvgPerPersonPassedMonth,
      dataPassedMonths,
      footerName,
      footerNamePerPerson,
      loading,
      refetch,
      totalLatestMonth,
      mainCaption
    } = useGetFollowUpMeetingProspectDashboardByMonth(3, headcounts)

    const onClickDetail = (monthSelected = null) => {
      const { endDate, userUid, organization_unit } = resolveDashboardWidgetFiltersFromContextFilters(analyticsContext)

      const filters = {
        performer_user_uid: headcounts ? undefined : userUid,
        organization_unit,
        activity_date: convertMonthToDateRange(endDate),
        first_type: 'follow_up',
        role_code: 'performer',
        is_customer: false,
        status: { is_success: true },
        type: { medium_code: 'meeting' },
        used_user_uid: true
      }

      if (monthSelected) {
        filters.activity_date = monthSelected
      }

      props.onShowLeadsPopup({ type: 'activities', filters })
    }

    return (
      <Component
        avgFollowUpMeetingCurrentMonthPerPerson={avgCurrentMonthPerPerson}
        avgFollowUpMeetingPassedMonthData={dataAvgPerPersonPassedMonth}
        avgFollowUpMeetingPersonPassedMonth={avgPerPersonPassedMonth}
        avgFollowUpMeetingThreeMonth={avgPassedMonths}
        footerName={footerName}
        footerNamePerPerson={footerNamePerPerson}
        isLoading={loading}
        onClickDetail={onClickDetail}
        refetchData={() => refetch()}
        threeMonthAgoFollowUpMeeting={dataPassedMonths}
        totalFollowUpMeeting={totalLatestMonth}
        mainCaption={mainCaption}
        {...props}
      />
    )
  }

  return withLeadPopUpHoc(Wrapper)
}

export default widgetFollowUpMeetingStatusHoc
